import { createSlice } from '@reduxjs/toolkit';
import { translator } from 'translation/useTranslator';
import { getProblemDetail } from 'utils/errorMessageHandling';
import { axiosAmApiInstance as amApi } from '../../utils/axios';
import { dispatch } from '../store';
import { rejectWithError } from '../dispatchError';
import {
  CreateOrderRequest,
  Order,
  OrdersState,
  AddExtraPackageRequest,
  UpdateOrderRequest
} from '../../@types/orders';

const asOwnOrder = (order: Order): Order => ({
  ...order,
  heldByTenantId: null,
  heldByTenantName: null
});

const asFundedForChild = (order: Order): Order => ({
  ...order,
  fundedByTenantId: null,
  fundedByTenantName: null
});

const initialState: OrdersState = {
  loading: {},
  orders: [],
  fundedForChildren: [],
  fetched: false
};

const slice = createSlice({
  name: 'tenantOrders',
  initialState,
  reducers: {
    startLoading(state, action) {
      state.loading[action.payload] = true;
    },
    stopLoading(state, action) {
      state.loading[action.payload] = false;
    },
    getOrdersSuccess(state, action) {
      state.orders = action.payload;
      state.fetched = true;
      state.loading.orders = false;
    },
    getFundedChildOrdersSuccess(state, action) {
      state.fundedForChildren = action.payload;
      state.loading.fundedForChildren = false;
    }
  }
});

export function getTenantOrders() {
  return async () => {
    dispatch(slice.actions.startLoading('orders'));
    try {
      const response = await amApi.get('/orders');
      dispatch(slice.actions.getOrdersSuccess((response.data?.orders ?? []).map(asOwnOrder)));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.ordersFetched() ?? 'Orders fetched'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('orders'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage: translator?.couldNotFetchOrders() ?? 'Could not fetch orders'
      });
    }
  };
}

export function createTenantOrder(request: CreateOrderRequest) {
  return async () => {
    dispatch(slice.actions.startLoading('save'));
    try {
      const response = await amApi.post('/orders', request);
      dispatch(getTenantOrders());
      dispatch(slice.actions.stopLoading('save'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.orderAdded() ?? 'Order added'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('save'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          getProblemDetail(error) ?? translator?.couldNotAddOrder() ?? 'Could not add order'
      });
    }
  };
}

export function getFundedChildOrders() {
  return async () => {
    dispatch(slice.actions.startLoading('fundedForChildren'));
    try {
      const response = await amApi.get('/child-tenants/orders');
      dispatch(
        slice.actions.getFundedChildOrdersSuccess(
          (response.data?.orders ?? []).map(asFundedForChild)
        )
      );

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.ordersFetched() ?? 'Orders fetched'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('fundedForChildren'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          getProblemDetail(error) ?? translator?.couldNotFetchOrders() ?? 'Could not fetch orders'
      });
    }
  };
}

export function createFundedOrder({
  tenantId,
  request
}: {
  tenantId: string;
  request: CreateOrderRequest;
}) {
  return async () => {
    dispatch(slice.actions.startLoading('save'));
    try {
      const response = await amApi.post(`/child-tenants/${tenantId}/orders`, request);
      dispatch(getTenantOrders());
      dispatch(getFundedChildOrders());
      dispatch(slice.actions.stopLoading('save'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.fundedOrderAdded() ?? 'Funded order added'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('save'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          getProblemDetail(error) ?? translator?.couldNotAddOrder() ?? 'Could not add order'
      });
    }
  };
}

export function updateFundedOrder({
  tenantId,
  orderId,
  request
}: {
  tenantId: string;
  orderId: string;
  request: UpdateOrderRequest;
}) {
  return async () => {
    dispatch(slice.actions.startLoading('save'));
    try {
      const response = await amApi.patch(`/child-tenants/${tenantId}/orders/${orderId}`, request);
      dispatch(getTenantOrders());
      dispatch(getFundedChildOrders());
      dispatch(slice.actions.stopLoading('save'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.orderUpdated() ?? 'Order updated'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('save'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          getProblemDetail(error) ?? translator?.couldNotUpdateOrder() ?? 'Could not update order'
      });
    }
  };
}

export function updateTenantOrder({ id, request }: { id: string; request: UpdateOrderRequest }) {
  return async () => {
    dispatch(slice.actions.startLoading('save'));
    try {
      const response = await amApi.patch(`/orders/${id}`, request);
      dispatch(getTenantOrders());
      dispatch(slice.actions.stopLoading('save'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.orderUpdated() ?? 'Order updated'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('save'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          getProblemDetail(error) ?? translator?.couldNotUpdateOrder() ?? 'Could not update order'
      });
    }
  };
}

export function addExtraPackage({ order, amount }: { order: Order; amount: number }) {
  return async () => {
    dispatch(slice.actions.startLoading('extraPackage'));
    const heldForChild = order.heldByTenantId !== null;
    const path = heldForChild
      ? `/child-tenants/${order.heldByTenantId}/orders/${order.id}/packages`
      : `/orders/${order.id}/packages`;
    const request: AddExtraPackageRequest = { quantity: amount };

    try {
      const response = await amApi.post(path, request);
      dispatch(heldForChild ? getFundedChildOrders() : getTenantOrders());
      dispatch(slice.actions.stopLoading('extraPackage'));

      return await Promise.resolve({
        result: response,
        defaultSuccessMessage: translator?.extraLimitSaved() ?? 'Extra usage limit saved'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading('extraPackage'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          getProblemDetail(error) ??
          translator?.couldNotSaveExtraPackage() ??
          'Could not save the extra usage limit'
      });
    }
  };
}

export default slice.reducer;
